import React from "react";
import axios from "axios";

const API_BASE = "/api";

function defaultContent() {
  return {
    brand: { name: "DALINI", logoUrl: "" },
    nav: [
      { label: "Каталог", to: "/catalog" },
      { label: "О компании", to: "/about" },
      { label: "Контакты", to: "/contacts" },
    ],
    seo: {
      siteName: "DALINI",
      defaultTitle: "DALINI",
      defaultDescription: "",
      defaultOgImage: "",
      canonicalBase: "https://dalini.by",
      routes: {},
      products: {},
    },
    home: { slides: [], categories: [], benefits: [] },
    contacts: { phone: "", address: "", hours: "" },
    company: {
      legalName: "",
      address: "",
      unp: "",
      okpo: "",
      iban: "",
      bank: "",
      swift: "",
      email: "",
      phone: "",
      director: "",
    },
    pages: { about: { title: "О компании", blocks: [] } },
    uiTexts: {
      footerContactsTitle: "Контакты",
      footerMessageTitle: "Сообщение",
      footerMessageBody: "Менеджеры компании с радостью ответят на ваши вопросы.",
      contactCtaLabel: "Отправьте нам сообщение",
      searchButtonAria: "Поиск",
      catalogTitle: "Каталог",
      notFoundTitle: "Ошибка 404",
      notFoundDescription: "Данная страница не существует. Попробуйте перейти на другую страницу сайта.",
      contactPageTitle: "Контакты",
      aboutPageTitleFallback: "О компании",
    },
    catalog: { categories: [], products: [] },
    version: 1,
  };
}

function ensureMetaTag(selector, attrs) {
  if (typeof document === "undefined") return null;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return el;
}

function applySeo({ title, description, ogImage, siteName, canonical }) {
  if (typeof document === "undefined") return;
  if (title) document.title = title;

  const desc = ensureMetaTag('meta[name="description"]', { name: "description" });
  if (desc && description) desc.setAttribute("content", description);

  const ogTitle = ensureMetaTag('meta[property="og:title"]', { property: "og:title" });
  if (ogTitle && title) ogTitle.setAttribute("content", title);

  const ogDesc = ensureMetaTag('meta[property="og:description"]', { property: "og:description" });
  if (ogDesc && description) ogDesc.setAttribute("content", description);

  const ogSite = ensureMetaTag('meta[property="og:site_name"]', { property: "og:site_name" });
  if (ogSite && siteName) ogSite.setAttribute("content", siteName);

  const ogImg = ensureMetaTag('meta[property="og:image"]', { property: "og:image" });
  if (ogImg && ogImage) ogImg.setAttribute("content", ogImage);

  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
  }
}

const ContentContext = React.createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = React.useState(defaultContent());
  const [isLoading, setIsLoading] = React.useState(true);

  const fetchFromServer = React.useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/content`);
    const serverContent = data?.content;
    if (!serverContent) {
      throw new Error("Invalid /api/content payload");
    }
    setContent(serverContent);
    return serverContent;
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        await fetchFromServer();
      } catch (err) {
        console.error("Failed to fetch content from server", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchFromServer]);

  const update = React.useCallback((path, nextValue) => {
    setContent((prev) => {
      const cloned = structuredClone(prev);
      const parts = path.split(".");
      let cur = cloned;
      for (let i = 0; i < parts.length - 1; i += 1) {
        cur[parts[i]] = cur[parts[i]] ?? {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = nextValue;
      return cloned;
    });
  }, []);

  const save = React.useCallback(async (data = content) => {
    const { data: response } = await axios.post(`${API_BASE}/content`, { content: data }, { withCredentials: true });
    if (response?.version) {
      setContent((prev) => ({ ...prev, version: response.version }));
    }
    return response;
  }, [content]);

  const refetch = React.useCallback(async () => {
    await fetchFromServer();
  }, [fetchFromServer]);

  const value = React.useMemo(
    () => ({ content, setContent, update, save, refetch, isLoading }),
    [content, update, save, refetch, isLoading]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = React.useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}

export function useSeo(routePath) {
  const { content } = useContent();

  React.useEffect(() => {
    const route = content?.seo?.routes?.[routePath];
    const title = route?.title || content?.seo?.defaultTitle;
    const description = route?.description || content?.seo?.defaultDescription;
    const ogImage = route?.ogImage || content?.seo?.defaultOgImage;
    const canonicalBase = (content?.seo?.canonicalBase || "").replace(/\/$/, "");
    const canonical = canonicalBase ? `${canonicalBase}${routePath}` : "";

    applySeo({
      title,
      description,
      ogImage,
      canonical,
      siteName: content?.seo?.siteName || content?.brand?.name,
    });
  }, [content, routePath]);
}
